import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";

const initialRouteForm = {
  route_id: "",
  r_name: "",
  r_from: "",
  r_to: "",
  r_from_lat: "",
  r_from_lng: "",
  r_to_lat: "",
  r_to_lng: "",
};

const initialCheckpoint = {
  checkpoints_id: "",
  checkpoint_name: "",
  lat: "",
  lng: "",
};

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function createPinIcon({
  label,
  bg = "#2563eb",
  border = "#ffffff",
  textColor = "#ffffff",
}) {
  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createPinIcon({ label: "F", bg: "#16a34a" });
const toIcon = createPinIcon({ label: "T", bg: "#dc2626" });

function checkpointIcon(index) {
  return createPinIcon({
    label: String(index + 1),
    bg: "#2563eb",
  });
}

export default function RouteMaster() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [successMessage, setSuccessMessage] = useState(
    params.get("success") === "1"
      ? "Route, checkpoints, and QR codes saved successfully!"
      : ""
  );
  const [errorMessage, setErrorMessage] = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialRouteForm);
  const [checkpoints, setCheckpoints] = useState([{ ...initialCheckpoint }]);
  const [activeTarget, setActiveTarget] = useState(null);

  const activeTargetRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);
  const hasAutoFitRef = useRef(false);

  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const setLatLngOnActiveTarget = useCallback((lat, lng, shouldCenter = false, label = "") => {
    const target = activeTargetRef.current;

    if (!target) {
      setErrorMessage("Please click Pick on Map for From, To, or Checkpoint first.");
      return;
    }

    const lat7 = Number(lat).toFixed(7);
    const lng7 = Number(lng).toFixed(7);

    if (target === "from") {
      setForm((prev) => ({
        ...prev,
        r_from_lat: lat7,
        r_from_lng: lng7,
        r_from: label || prev.r_from,
      }));
    } else if (target === "to") {
      setForm((prev) => ({
        ...prev,
        r_to_lat: lat7,
        r_to_lng: lng7,
        r_to: label || prev.r_to,
      }));
    } else if (typeof target === "object" && target.type === "checkpoint") {
      setCheckpoints((prev) =>
        prev.map((checkpoint, index) =>
          index === target.index
            ? {
                ...checkpoint,
                lat: lat7,
                lng: lng7,
              }
            : checkpoint
        )
      );
    }

    if (shouldCenter && mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name: localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor: localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone: localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email: localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo: localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, editId]);

useEffect(() => {
  if (loading) return;
  if (!mapRef.current || mapInstanceRef.current) return;

  const map = L.map(mapRef.current, { zoomControl: true }).setView(
    [20.5937, 78.9629],
    5
  );

  mapInstanceRef.current = map;

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap",
  }).addTo(map);

  const geocoder = L.Control.geocoder({ defaultMarkGeocode: false })
    .on("markgeocode", (e) => {
      const center = e.geocode.center;

      if (e.geocode.bbox) {
        map.fitBounds(e.geocode.bbox);
      } else {
        map.setView(center, 16);
      }

      setLatLngOnActiveTarget(center.lat, center.lng, false, e.geocode.name || "");
    })
    .addTo(map);

  geocoderRef.current = geocoder;

  map.on("click", (ev) => {
    setLatLngOnActiveTarget(ev.latlng.lat, ev.latlng.lng, true);
  });

  setTimeout(() => map.invalidateSize(), 100);
  setTimeout(() => map.invalidateSize(), 500);
  setTimeout(() => map.invalidateSize(), 1000);

  return () => {
    map.remove();
    mapInstanceRef.current = null;
    geocoderRef.current = null;
  };
}, [loading, setLatLngOnActiveTarget]);

useEffect(() => {
  if (loading) return;
  if (!mapInstanceRef.current) return;
  syncMarkers(false);
}, [loading, form, checkpoints]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);

      const requests = [
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(`${API_BASE_URL}/route/${editId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
        );
      }

      const [dashboardRes, routeRes] = await Promise.all(requests);

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(companyDetails.admin_active ?? local.admin_active ?? 0),
        company_address: companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (editId) {
        if (!routeRes?.ok) {
          throw new Error("Failed to fetch route details");
        }

        const routeResult = await routeRes.json();
        const route = routeResult?.data?.route || routeResult?.data || {};
        const routeCheckpoints = routeResult?.data?.checkpoints || route?.checkpoints || [];

        setForm({
          route_id: route.route_id || route.id || editId,
          r_name: route.r_name || "",
          r_from: route.r_from || "",
          r_to: route.r_to || "",
          r_from_lat: valueOrEmpty(route.r_from_lat),
          r_from_lng: valueOrEmpty(route.r_from_lng),
          r_to_lat: valueOrEmpty(route.r_to_lat),
          r_to_lng: valueOrEmpty(route.r_to_lng),
        });

        setCheckpoints(
          Array.isArray(routeCheckpoints) && routeCheckpoints.length > 0
            ? routeCheckpoints.map((item) => ({
                checkpoints_id: item.checkpoints_id || item.id || "",
                checkpoint_name: item.checkpoint_name || "",
                lat: valueOrEmpty(item.lat),
                lng: valueOrEmpty(item.lng),
              }))
            : [{ ...initialCheckpoint }]
        );
      }
    } catch (error) {
      console.error("Route master page error:", error);
      setErrorMessage(error.message || "Failed to load page data.");
    } finally {
      setLoading(false);
    }
  };

  const triggerFenceSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/route/${routeId}/fence-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.warn("Fence sync failed:", error);
    }
  };

  const triggerQrSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/route/${routeId}/qr-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.warn("QR sync failed:", error);
    }
  };

  const syncMarkers = (forceFit = false) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const fLat = parseFloat(form.r_from_lat || "");
    const fLng = parseFloat(form.r_from_lng || "");

    if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
      if (fromMarkerRef.current) {
        fromMarkerRef.current.setLatLng([fLat, fLng]);
        fromMarkerRef.current.setPopupContent(`From: ${form.r_from || "Selected location"}`);
      } else {
        fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.r_from || "Selected location"}`);
      }
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    const tLat = parseFloat(form.r_to_lat || "");
    const tLng = parseFloat(form.r_to_lng || "");

    if (!Number.isNaN(tLat) && !Number.isNaN(tLng)) {
      if (toMarkerRef.current) {
        toMarkerRef.current.setLatLng([tLat, tLng]);
        toMarkerRef.current.setPopupContent(`To: ${form.r_to || "Selected location"}`);
      } else {
        toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.r_to || "Selected location"}`);
      }
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    checkpointMarkersRef.current.forEach((marker) => {
      if (marker) map.removeLayer(marker);
    });

    checkpointMarkersRef.current = checkpoints.map((checkpoint, index) => {
      const lat = parseFloat(checkpoint.lat || "");
      const lng = parseFloat(checkpoint.lng || "");

      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

      return L.marker([lat, lng], { icon: checkpointIcon(index) })
        .addTo(map)
        .bindPopup(
          `Checkpoint ${index + 1}: ${checkpoint.checkpoint_name || "Selected location"}`
        );
    });

    const layers = [
      fromMarkerRef.current,
      toMarkerRef.current,
      ...checkpointMarkersRef.current,
    ].filter(Boolean);

    if (layers.length > 0 && (forceFit || !hasAutoFitRef.current)) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      hasAutoFitRef.current = true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckpointChange = (index, field, value) => {
    setCheckpoints((prev) =>
      prev.map((checkpoint, i) =>
        i === index ? { ...checkpoint, [field]: value } : checkpoint
      )
    );
  };

  const addCheckpoint = () => {
    setCheckpoints((prev) => [...prev, { ...initialCheckpoint }]);
  };

  const removeCheckpoint = (index) => {
    setCheckpoints((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ ...initialCheckpoint }];
    });
  };

  const geocodeAddress = (target, text) => {
    const geocoder = geocoderRef.current;

    if (!geocoder?._geocoder || !text.trim()) {
      setErrorMessage("Enter a location name first.");
      return;
    }

    setActiveTarget(target);
    activeTargetRef.current = target;

    geocoder._geocoder.geocode(text, (results) => {
      if (results && results[0] && results[0].center) {
        const center = results[0].center;
        const lat7 = Number(center.lat).toFixed(7);
        const lng7 = Number(center.lng).toFixed(7);

        if (target === "from") {
          setForm((prev) => ({
            ...prev,
            r_from: results[0].name || text,
            r_from_lat: lat7,
            r_from_lng: lng7,
          }));
        } else if (target === "to") {
          setForm((prev) => ({
            ...prev,
            r_to: results[0].name || text,
            r_to_lat: lat7,
            r_to_lng: lng7,
          }));
        } else if (typeof target === "object" && target.type === "checkpoint") {
          setCheckpoints((prev) =>
            prev.map((checkpoint, index) =>
              index === target.index
                ? {
                    ...checkpoint,
                    lat: lat7,
                    lng: lng7,
                  }
                : checkpoint
            )
          );
        }

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([center.lat, center.lng], 16);
        }
      } else {
        setErrorMessage("Location not found.");
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.r_name.trim() || !form.r_from.trim() || !form.r_to.trim()) {
      setErrorMessage("Route Name, From, and To are required.");
      return;
    }

    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const payload = {
        r_name: form.r_name.trim(),
        r_from: form.r_from.trim(),
        r_to: form.r_to.trim(),
        r_from_lat: form.r_from_lat || null,
        r_from_lng: form.r_from_lng || null,
        r_to_lat: form.r_to_lat || null,
        r_to_lng: form.r_to_lng || null,
        checkpoints: checkpoints
          .map((checkpoint) => ({
            checkpoints_id: checkpoint.checkpoints_id || null,
            checkpoint_name: checkpoint.checkpoint_name.trim(),
            lat: checkpoint.lat || null,
            lng: checkpoint.lng || null,
          }))
          .filter((checkpoint) => checkpoint.checkpoint_name),
      };

      const url = isEdit
        ? `${API_BASE_URL}/route/${form.route_id || editId}`
        : `${API_BASE_URL}/route`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save route");
      }

      const savedRouteId = data?.data?.route_id || data?.data?.id || form.route_id || editId;

      if (savedRouteId) {
        await triggerFenceSync(savedRouteId, token);
        await triggerQrSync(savedRouteId, token);
      }

      if (isEdit) {
        setSuccessMessage("Route, checkpoints, and QR codes updated successfully!");
      } else {
        navigate("/route_list");
      }
    } catch (error) {
      console.error("Save route error:", error);
      setErrorMessage(error.message || "Failed to save route.");
    } finally {
      setSaving(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const escortPage = useMemo(() => "/escort_master", []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading route management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="routes"
      activeMenu="routes"
    >
      <PageHeader
        isEdit={isEdit}
        onEscort={() => navigate(escortPage)}
        onList={() => navigate("/route_list")}
      />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage ? <Alert type="error" message={errorMessage} /> : null}

      <FormSection
        title={isEdit ? "Edit Route" : "New Route"}
        subtitle="Create patrol routes, assign checkpoints, generate QR codes, and pick coordinates directly from the map"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field label="Route Name *">
              <Input
                type="text"
                name="r_name"
                value={form.r_name}
                onChange={handleChange}
                placeholder="Enter route name"
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
            <LocationField
              label="From *"
              name="r_from"
              value={form.r_from}
              lat={form.r_from_lat}
              lng={form.r_from_lng}
              onChange={handleChange}
              onPick={() => {
                setActiveTarget("from");
                activeTargetRef.current = "from";
                window.alert("Click on the map or use the search box to set: From");
              }}
              onSearch={() => geocodeAddress("from", form.r_from)}
            />

            <LocationField
              label="To *"
              name="r_to"
              value={form.r_to}
              lat={form.r_to_lat}
              lng={form.r_to_lng}
              onChange={handleChange}
              onPick={() => {
                setActiveTarget("to");
                activeTargetRef.current = "to";
                window.alert("Click on the map or use the search box to set: To");
              }}
              onSearch={() => geocodeAddress("to", form.r_to)}
            />
          </div>

          <div className="mt-6">
            <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
              <div>
                <h3
                  className="m-0 text-lg font-bold text-slate-900"
                  style={{ fontFamily: FONT_UI }}
                >
                  Checkpoint Details
                </h3>
                <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
                  Add one or more checkpoints for this patrol route
                </p>
              </div>

              <button
                type="button"
                onClick={addCheckpoint}
                className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
                style={{ fontFamily: FONT_UI }}
              >
                + Add Checkpoint
              </button>
            </div>

            <div className="grid gap-4">
              {checkpoints.map((checkpoint, index) => (
                <div
                  key={`${checkpoint.checkpoints_id || "new"}-${index}`}
                  className="border border-slate-200 rounded-2xl p-5 bg-slate-50"
                >
                  <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
                    <div
                      className="px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 font-bold text-xs"
                      style={{ fontFamily: FONT_UI }}
                    >
                      Checkpoint {index + 1}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeCheckpoint(index)}
                      className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600"
                      style={{ fontFamily: FONT_UI }}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Checkpoint Name *">
                      <Input
                        type="text"
                        value={checkpoint.checkpoint_name}
                        onChange={(e) =>
                          handleCheckpointChange(index, "checkpoint_name", e.target.value)
                        }
                        placeholder="Checkpoint name"
                        required
                      />
                    </Field>

                    <Field label="Latitude">
                      <Input type="text" value={checkpoint.lat} readOnly placeholder="Lat" />
                    </Field>

                    <Field label="Longitude">
                      <Input type="text" value={checkpoint.lng} readOnly placeholder="Lng" />
                    </Field>
                  </div>

                  <div className="flex gap-3 flex-wrap mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        const target = { type: "checkpoint", index };
                        setActiveTarget(target);
                        activeTargetRef.current = target;
                        window.alert(`Click on the map to set: Checkpoint ${index + 1}`);
                      }}
                      className="px-4 py-2.5 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"
                      style={{ fontFamily: FONT_UI }}
                    >
                      Pick on Map
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        geocodeAddress(
                          { type: "checkpoint", index },
                          checkpoint.checkpoint_name || `Checkpoint ${index + 1}`
                        )
                      }
                      className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
                      style={{ fontFamily: FONT_UI }}
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={saving}
              className="w-full px-5 py-3 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ fontFamily: FONT_UI }}
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update Route & Generate QR"
                : "Save Route & Generate QR"}
            </button>
          </div>
        </form>
      </FormSection>

      <FormSection
        title="Route Map"
        subtitle="Search, click, and confirm exact map coordinates for the route and checkpoints"
      >
        <div
          ref={mapRef}
          className="w-full h-[420px] rounded-2xl overflow-hidden border border-slate-200 bg-slate-200"
        />
      </FormSection>

      <style>{`
        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </Layout>
  );
}

function PageHeader({ isEdit, onEscort, onList }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Route Management
        </h2>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {isEdit ? "Edit an existing patrol route" : "Create a new patrol route"}
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            {isEdit ? "Edit Route" : "New Route"}
          </h3>
          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Manage route name, start and end points, checkpoint coordinates, and QR codes
          </p>
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={onEscort}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
            style={{
              background: skin.gradient,
              fontFamily: FONT_UI,
            }}
          >
            Escort Route
          </button>

          <button
            type="button"
            onClick={onList}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Route List
          </button>
        </div>
      </div>
    </>
  );
}

function LocationField({ label, name, value, lat, lng, onChange, onPick, onSearch }) {
  return (
    <Field label={label}>
      <div className="grid gap-3">
        <Input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearch();
            }
          }}
          placeholder="Search or click on map"
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input type="text" value={lat} readOnly placeholder="Lat" />
          <Input type="text" value={lng} readOnly placeholder="Lng" />
        </div>

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={onPick}
            className="px-4 py-2.5 rounded-xl bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600"
            style={{ fontFamily: FONT_UI }}
          >
            Pick on Map
          </button>

          <button
            type="button"
            onClick={onSearch}
            className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            style={{ fontFamily: FONT_UI }}
          >
            Search Location
          </button>
        </div>
      </div>
    </Field>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
      <div className="mb-5">
        <h3
          className="m-0 text-[22px] font-normal text-slate-900"
          style={{ fontFamily: FONT_HEADING }}
        >
          {title}
        </h3>
        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          {subtitle}
        </p>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="block mb-2 text-sm font-semibold text-slate-900"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}